import React, {useEffect, useState} from "react";
import Loader from "../../components/Loader";
import axiosInstance from "../../utils/axiosInstance";
import {IoArrowBackSharp} from "react-icons/io5";

const EmailForm = () => {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [emails, setEmails] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const response = await axiosInstance.get("/emails");
                setEmails(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch users:", error);
            }
        };
        fetchEmails();
    }, []);

    const handleCheckboxChange = (email) => {
        setSelectedUsers((prevSelectedUsers) => {
            if (prevSelectedUsers.includes(email)) {
                return prevSelectedUsers.filter((e) => e !== email);
            } else {
                return [...prevSelectedUsers, email];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(emails);
        }
        setSelectAll(!selectAll);
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axiosInstance.post("/emails/send", {
                subject,
                body,
                recipients: selectedUsers,
            });
            if (response.status === 200) {
                setMessage("Emails sent successfully!");
            }
        } catch (error) {
            setMessage("Failed to send emails.");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendEmailAsync = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.post("/emails/send/async", {
                subject,
                body,
                recipients: selectedUsers,
            });
            if (response.status === 200) {
                setMessage("Emails async started successfully!");
            }
        } catch (error) {
            setMessage("Failed to start async emails send process.");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredEmails = emails.filter((email) =>
        email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex justify-center bg-gray-200 my-12">
            <div className="bg-white shadow-md rounded-lg p-6 max-w-md w-full">
                <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
                    {/* Go Back Button */}
                    <button
                        onClick={() => window.history.back()} // This will navigate back to the previous page
                        className="rounded-full hover:bg-gray-200 transition-all mr-2"
                    >
                        <IoArrowBackSharp size={24}/>
                    </button>
                    {" "}
                    Send Email to Selected Users
                </h3>
                {message && (
                    <p
                        className={`text-center mb-4 ${
                            message.includes("successfully")
                                ? "text-green-600"
                                : "text-red-600"
                        }`}
                    >
                        {message}
                    </p>
                )}
                <form onSubmit={handleSendEmail}>
                    <div className="mb-4">
                        <label
                            htmlFor="subject"
                            className="block text-gray-700 font-medium mb-2"
                        >
                            Subject
                        </label>
                        <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label
                            htmlFor="body"
                            className="block text-gray-700 font-medium mb-2"
                        >
                            Body (supports HTML)
                        </label>
                        <textarea
                            id="body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <input
                            type="checkbox"
                            id="select-all"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="mr-2"
                        />
                        <label htmlFor="select-all" className="text-gray-700">
                            Select All
                        </label>

                        {/* Search bar */}
                        <div className="mt-2 mb-2">
                            <input
                                type="text"
                                placeholder="Search emails..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Email list */}
                        <div className="h-32 overflow-y-scroll border rounded-md p-2">
                            {loading ? (
                                <Loader />
                            ) : filteredEmails.length > 0 ? (
                                filteredEmails.map((email) => (
                                    <div key={email} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`user-${email}`}
                                            checked={selectedUsers.includes(email)}
                                            onChange={() => handleCheckboxChange(email)}
                                            className="mr-2 cursor-pointer"
                                        />
                                        <label htmlFor={`user-${email}`} className="text-gray-700">
                                            {email}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm">No results found</p>
                            )}
                        </div>
                    </div>

                    {/* Normal send button */}
                    <button
                        type="submit"
                        className="w-full bg-blue-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-600 transition-colors duration-200 mb-2"
                        disabled={isSubmitting || selectedUsers.length === 0}
                    >
                        {isSubmitting ? "Sending..." : "Send Email"}
                    </button>

                    {/* Async send button */}
                    <button
                        type="button"
                        onClick={handleSendEmailAsync}
                        className="w-full bg-green-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-600 transition-colors duration-200"
                        disabled={isSubmitting || selectedUsers.length === 0}
                    >
                        {isSubmitting ? "Starting Async..." : "Send Email Async"}
                    </button>
                </form>

            </div>
        </div>
    );
};

export default EmailForm;
